import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/common/EmptyState';
import { Badge } from '../components/ui/badge';

// SLA durations are stored in minutes, but minutes are unfriendly to read
// ("960 минут"?). This renders a value + unit picker (минуты/часы/дни) with a
// live human-readable readout, while still emitting minutes to the form.
const UNIT_FACTORS: Record<'m' | 'h' | 'd', number> = { m: 1, h: 60, d: 1440 };

const humanizeMinutes = (m: number): string => {
  if (!m || m <= 0) return '0 мин';
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const min = m % 60;
  const parts: string[] = [];
  if (d) parts.push(`${d} дн`);
  if (h) parts.push(`${h} ч`);
  if (min) parts.push(`${min} мин`);
  return parts.join(' ');
};

const DurationField: React.FC<{
  label: string;
  minutes: number;
  onChange: (minutes: number) => void;
  help?: string;
}> = ({ label, minutes, onChange, help }) => {
  const pickUnit = (val: number): 'm' | 'h' | 'd' => {
    if (val && val % 1440 === 0) return 'd';
    if (val && val % 60 === 0) return 'h';
    return 'm';
  };
  const [unit, setUnit] = useState<'m' | 'h' | 'd'>(() => pickUnit(minutes));
  const factor = UNIT_FACTORS[unit];
  const amountRaw = minutes / factor;
  const amount = Number.isInteger(amountRaw) ? amountRaw : Math.round(amountRaw * 100) / 100;

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">{label}</label>
      <div className="flex gap-2">
        <Input
          type="number"
          min="0"
          step={unit === 'm' ? 1 : 0.5}
          value={amount}
          onChange={(e) => onChange(Math.max(0, Math.round((parseFloat(e.target.value) || 0) * factor)))}
          className="flex-1"
        />
        <select
          value={unit}
          onChange={(e) => setUnit(e.target.value as 'm' | 'h' | 'd')}
          className="h-10 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="m">минуты</option>
          <option value="h">часы</option>
          <option value="d">дни</option>
        </select>
      </div>
      <p className="text-xs text-primary mt-1">= {humanizeMinutes(minutes)}</p>
      {help && <p className="text-xs text-muted-foreground mt-0.5">{help}</p>}
    </div>
  );
};

interface SLASettings {
  id: string;
  firstResponseTime: number;
  firstResponseTimeFormatted: string;
  resolutionTime: number;
  resolutionTimeFormatted: string;
  secondLineFirstResponseTime: number;
  secondLineResolutionTime: number;
  priorityMultipliers: {
    low: number;
    medium: number;
    high: number;
    urgent: number;
  };
  categoryMultipliers: {
    general: number;
    technical: number;
    billing: number;
    support: number;
  };
  workingHours: {
    start: number;
    end: number;
  };
  workingDays: number[];
  notifications: {
    firstResponseWarning: number;
    resolutionWarning: number;
  };
  description: string;
  version: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface TestCalculationData {
  createdAt: Date;
  priority: string;
  category: string;
  firstResponseDeadline: Date;
  resolutionDeadline: Date;
  firstResponseTimeFormatted: string;
  resolutionTimeFormatted: string;
}

const SLASettingsPage: React.FC = () => {
  const { user, token } = useAuthStore();
  const [settings, setSettings] = useState<SLASettings[]>([]);
  const [activeSettings, setActiveSettings] = useState<SLASettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingSettings, setEditingSettings] = useState<SLASettings | null>(null);
  const [showTestForm, setShowTestForm] = useState(false);
  const [testResult, setTestResult] = useState<TestCalculationData | null>(null);

  // Форма создания/редактирования
  const [formData, setFormData] = useState({
    firstResponseTime: 30,
    resolutionTime: 480,
    secondLineFirstResponseTime: 60,
    secondLineResolutionTime: 960,
    priorityMultipliers: {
      low: 1.5,
      medium: 1.0,
      high: 0.7,
      urgent: 0.5
    },
    categoryMultipliers: {
      general: 1.0,
      technical: 1.2,
      billing: 0.8,
      support: 1.0
    },
    workingHours: {
      start: 540, // 9:00
      end: 1080   // 18:00
    },
    workingDays: [1, 2, 3, 4, 5, 6, 0], // Все дни недели (включая выходные)
    notifications: {
      firstResponseWarning: 15,
      resolutionWarning: 60
    },
    description: ''
  });

  // Форма тестирования
  const [testFormData, setTestFormData] = useState({
    createdAt: new Date().toISOString().slice(0, 16),
    priority: 'medium',
    category: 'general'
  });

  useEffect(() => {
    if (user && user.role === 'superadmin') {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      // Trailing slash is required — Django routes the collection at
      // `api/sla-settings/`. Without it APPEND_SLASH 301-redirects, and on a
      // POST the browser downgrades 301 to GET and drops the body, so creating
      // settings silently fails.
      const response = await fetch('/api/sla-settings/', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка загрузки настроек SLA');
      }

      const data = await response.json();
      setSettings(data.settings);
      
      // Находим активные настройки
      const active = data.settings.find((s: SLASettings) => s.isActive);
      setActiveSettings(active || null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveSettings = async () => {
    try {
      const response = await fetch('/api/sla-settings/active', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setActiveSettings(data.settings);
      }
    } catch (err) {
      console.error('Error fetching active settings:', err);
    }
  };

  const handleCreateSettings = async () => {
    try {
      const response = await fetch('/api/sla-settings/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка создания настроек');
      }

      setShowCreateForm(false);
      resetFormData();
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    }
  };

  const handleEditSettings = async () => {
    if (!editingSettings) return;

    try {
      const response = await fetch(`/api/sla-settings/${editingSettings.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Ошибка обновления настроек');
      }

      setShowEditForm(false);
      setEditingSettings(null);
      resetFormData();
      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    }
  };

  const startEditSettings = (setting: SLASettings) => {
    setEditingSettings(setting);
    setFormData({
      firstResponseTime: setting.firstResponseTime,
      resolutionTime: setting.resolutionTime,
      secondLineFirstResponseTime: setting.secondLineFirstResponseTime ?? 60,
      secondLineResolutionTime: setting.secondLineResolutionTime ?? 960,
      priorityMultipliers: { ...setting.priorityMultipliers },
      categoryMultipliers: { ...setting.categoryMultipliers },
      workingHours: { ...setting.workingHours },
      workingDays: [...setting.workingDays],
      notifications: { ...setting.notifications },
      description: setting.description
    });
    setShowEditForm(true);
  };

  const resetFormData = () => {
    setFormData({
      firstResponseTime: 30,
      resolutionTime: 480,
      secondLineFirstResponseTime: 60,
      secondLineResolutionTime: 960,
      priorityMultipliers: {
        low: 1.5,
        medium: 1.0,
        high: 0.7,
        urgent: 0.5
      },
      categoryMultipliers: {
        general: 1.0,
        technical: 1.2,
        billing: 0.8,
        support: 1.0
      },
      workingHours: {
        start: 540,
        end: 1080
      },
      workingDays: [1, 2, 3, 4, 5, 6, 0],
      notifications: {
        firstResponseWarning: 15,
        resolutionWarning: 60
      },
      description: ''
    });
  };

  const handleActivateSettings = async (id: string) => {
    try {
      const response = await fetch(`/api/sla-settings/${id}/activate`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка активации настроек');
      }

      await fetchSettings();
      await fetchActiveSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    }
  };

  const handleDeleteSettings = async (id: string) => {
    if (!window.confirm('Вы уверены, что хотите удалить эти настройки?')) {
      return;
    }

    try {
      const response = await fetch(`/api/sla-settings/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка удаления настроек');
      }

      await fetchSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    }
  };

  const handleTestCalculation = async () => {
    try {
      const response = await fetch('/api/sla-settings/test-calculation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testFormData)
      });

      if (!response.ok) {
        throw new Error('Ошибка тестирования расчета');
      }

      const data = await response.json();
      setTestResult(data.testData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
    }
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}ч ${mins}м` : `${mins}м`;
  };

  const formatWorkingHours = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const formatWorkingDays = (days: number[]) => {
    const dayNames = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
    return days.map(day => dayNames[day]).join(', ');
  };

  const toggleWorkingDay = (day: number) => {
    setFormData(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day].sort()
    }));
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          <strong>Доступ запрещен!</strong> Требуются права superadmin для просмотра этой страницы.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-foreground">Настройки SLA</h1>
          <div className="space-x-2">
            <Button
              onClick={() => setShowTestForm(!showTestForm)}
              variant="default"
            >
              {showTestForm ? 'Скрыть тест' : 'Тест расчета'}
            </Button>
            <Button
              onClick={() => {
                setShowCreateForm(!showCreateForm);
                setShowEditForm(false);
                setEditingSettings(null);
                resetFormData();
              }}
              variant="default"
            >
              {showCreateForm ? 'Отмена' : 'Создать настройки'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded mb-6">
            {error}
            <Button
              onClick={() => setError(null)}
              variant="ghost"
              size="icon"
              className="float-right"
            >
              ×
            </Button>
          </div>
        )}

        {/* Форма создания/редактирования настроек */}
        {(showCreateForm || showEditForm) && (
          <div className="bg-card rounded-lg shadow-md p-6 mb-8 border border-border">
            <h2 className="text-xl font-semibold mb-4 text-foreground">
              {showEditForm ? 'Редактировать настройки SLA' : 'Создать новые настройки SLA'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DurationField
                label="Время на первый ответ (1-я линия)"
                minutes={formData.firstResponseTime}
                onChange={(m) => setFormData({ ...formData, firstResponseTime: m })}
                help="Сколько даётся на первый ответ клиенту"
              />

              <DurationField
                label="Время на решение (1-я линия)"
                minutes={formData.resolutionTime}
                onChange={(m) => setFormData({ ...formData, resolutionTime: m })}
                help="Сколько даётся на полное решение заявки"
              />

              <div className="md:col-span-2 mt-2 pt-4 border-t border-border">
                <h3 className="text-sm font-semibold text-foreground mb-1">2-я линия поддержки</h3>
                <p className="text-xs text-muted-foreground">
                  Отдельные тайминги для эскалированных заявок. Таймер стартует заново
                  с момента эскалации.
                </p>
              </div>

              <DurationField
                label="Время на первый ответ — 2-я линия"
                minutes={formData.secondLineFirstResponseTime}
                onChange={(m) => setFormData({ ...formData, secondLineFirstResponseTime: m })}
                help="Отсчёт стартует заново с момента эскалации"
              />

              <DurationField
                label="Время на решение — 2-я линия"
                minutes={formData.secondLineResolutionTime}
                onChange={(m) => setFormData({ ...formData, secondLineResolutionTime: m })}
                help="Обычно больше, чем у 1-й линии"
              />

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Описание
                </label>
                <Input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({
                    ...formData,
                    description: e.target.value
                  })}
                  placeholder="Описание настроек SLA"
                />
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-medium mb-1 text-foreground">Множители приоритетов</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Корректируют базовое время. <b>Меньше 1</b> — срочнее (быстрее), <b>больше 1</b> — больше времени. Напр. 0.5 = вдвое быстрее.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(formData.priorityMultipliers).map(([priority, multiplier]) => (
                  <div key={priority}>
                    <label className="block text-sm font-medium text-foreground mb-1 capitalize">
                      {priority === 'low' ? 'Низкий' : 
                       priority === 'medium' ? 'Средний' : 
                       priority === 'high' ? 'Высокий' : 'Срочный'}
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      value={multiplier}
                      onChange={(e) => setFormData({
                        ...formData,
                        priorityMultipliers: {
                          ...formData.priorityMultipliers,
                          [priority]: parseFloat(e.target.value) || 1.0
                        }
                      })}
                      min="0.1"
                      max="10"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-medium mb-1 text-foreground">Множители категорий</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Тот же принцип по категориям: итоговое время = базовое × множитель приоритета × множитель категории.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(formData.categoryMultipliers).map(([category, multiplier]) => (
                  <div key={category}>
                    <label className="block text-sm font-medium text-foreground mb-1 capitalize">
                      {category === 'general' ? 'Общие' : 
                       category === 'technical' ? 'Технические' : 
                       category === 'billing' ? 'Биллинг' : 'Поддержка'}
                    </label>
                    <Input
                      type="number"
                      step="0.1"
                      value={multiplier}
                      onChange={(e) => setFormData({
                        ...formData,
                        categoryMultipliers: {
                          ...formData.categoryMultipliers,
                          [category]: parseFloat(e.target.value) || 1.0
                        }
                      })}
                      min="0.1"
                      max="10"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-medium mb-3 text-foreground">Рабочие часы</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Начало рабочего дня
                  </label>
                  <Input
                    type="time"
                    value={`${Math.floor(formData.workingHours.start / 60).toString().padStart(2, '0')}:${(formData.workingHours.start % 60).toString().padStart(2, '0')}`}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      setFormData({
                        ...formData,
                        workingHours: {
                          ...formData.workingHours,
                          start: hours * 60 + minutes
                        }
                      });
                    }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Конец рабочего дня
                  </label>
                  <Input
                    type="time"
                    value={`${Math.floor(formData.workingHours.end / 60).toString().padStart(2, '0')}:${(formData.workingHours.end % 60).toString().padStart(2, '0')}`}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(':').map(Number);
                      setFormData({
                        ...formData,
                        workingHours: {
                          ...formData.workingHours,
                          end: hours * 60 + minutes
                        }
                      });
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-medium mb-3 text-foreground">Рабочие дни недели</h3>
              <div className="grid grid-cols-7 gap-2">
                {['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'].map((day, index) => (
                  <Button
                    key={index}
                    type="button"
                    onClick={() => toggleWorkingDay(index)}
                    variant={formData.workingDays.includes(index) ? "default" : "outline"}
                    className="p-3"
                  >
                    {day}
                  </Button>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                SLA действует только в выбранные дни недели
              </p>
            </div>

            <div className="mt-6">
              <h3 className="text-lg font-medium mb-1 text-foreground">Предупреждения</h3>
              <p className="text-xs text-muted-foreground mb-3">
                За сколько до дедлайна напоминать оператору, что время на исходе.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <DurationField
                  label="Предупредить до дедлайна первого ответа"
                  minutes={formData.notifications.firstResponseWarning}
                  onChange={(m) => setFormData({
                    ...formData,
                    notifications: { ...formData.notifications, firstResponseWarning: m },
                  })}
                />
                <DurationField
                  label="Предупредить до дедлайна решения"
                  minutes={formData.notifications.resolutionWarning}
                  onChange={(m) => setFormData({
                    ...formData,
                    notifications: { ...formData.notifications, resolutionWarning: m },
                  })}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <Button
                onClick={() => {
                  setShowCreateForm(false);
                  setShowEditForm(false);
                  setEditingSettings(null);
                  resetFormData();
                }}
                variant="outline"
              >
                Отмена
              </Button>
              <Button
                onClick={showEditForm ? handleEditSettings : handleCreateSettings}
                variant="default"
              >
                {showEditForm ? 'Сохранить изменения' : 'Создать настройки'}
              </Button>
            </div>
          </div>
        )}

        {/* Форма тестирования расчета */}
        {showTestForm && (
          <div className="bg-card rounded-lg shadow-md p-6 mb-8 border border-border">
            <h2 className="text-xl font-semibold mb-4 text-foreground">Тест расчета SLA</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Дата создания
                </label>
                <Input
                  type="datetime-local"
                  value={testFormData.createdAt}
                  onChange={(e) => setTestFormData({
                    ...testFormData,
                    createdAt: e.target.value
                  })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Приоритет
                </label>
                <select
                  value={testFormData.priority}
                  onChange={(e) => setTestFormData({
                    ...testFormData,
                    priority: e.target.value
                  })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="low">Низкий</option>
                  <option value="medium">Средний</option>
                  <option value="high">Высокий</option>
                  <option value="urgent">Срочный</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Категория
                </label>
                <select
                  value={testFormData.category}
                  onChange={(e) => setTestFormData({
                    ...testFormData,
                    category: e.target.value
                  })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="general">Общие</option>
                  <option value="technical">Технические</option>
                  <option value="billing">Биллинг</option>
                  <option value="support">Поддержка</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={handleTestCalculation}
                variant="default"
              >
                Рассчитать
              </Button>
            </div>

            {testResult && (
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h3 className="text-lg font-medium mb-3 text-foreground">Результат расчета</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Время на первый ответ:</p>
                    <p className="font-medium text-foreground">{testResult.firstResponseTimeFormatted}</p>
                    <p className="text-sm text-muted-foreground">Дедлайн:</p>
                    <p className="font-medium text-foreground">{new Date(testResult.firstResponseDeadline).toLocaleString('ru-RU')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Время на решение:</p>
                    <p className="font-medium text-foreground">{testResult.resolutionTimeFormatted}</p>
                    <p className="text-sm text-muted-foreground">Дедлайн:</p>
                    <p className="font-medium text-foreground">{new Date(testResult.resolutionDeadline).toLocaleString('ru-RU')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Список всех настроек */}
        <div className="bg-card rounded-lg shadow-md overflow-hidden border border-border">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">Все настройки SLA</h2>
          </div>

          {settings.length === 0 ? (
            <EmptyState 
              message="Настройки SLA не найдены. Создайте первые настройки."
            />
          ) : (
            <div className="divide-y divide-border">
              {settings.map((setting) => (
                <div key={setting.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-lg font-medium text-foreground">
                          Версия {setting.version}
                        </h3>
                        {setting.isActive && (
                          <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Активные
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-1">
                        {setting.description}
                      </p>
                      
                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Первый ответ:</span>
                          <span className="ml-2 font-medium text-foreground">{setting.firstResponseTimeFormatted}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Решение:</span>
                          <span className="ml-2 font-medium text-foreground">{setting.resolutionTimeFormatted}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">2-я линия (ответ/решение):</span>
                          <span className="ml-2 font-medium text-foreground">
                            {formatTime(setting.secondLineFirstResponseTime ?? 0)} / {formatTime(setting.secondLineResolutionTime ?? 0)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Рабочие часы:</span>
                          <span className="ml-2 font-medium text-foreground">
                            {formatWorkingHours(setting.workingHours.start)} - {formatWorkingHours(setting.workingHours.end)}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Рабочие дни:</span>
                          <span className="ml-2 font-medium text-foreground">{formatWorkingDays(setting.workingDays)}</span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        Создано: {new Date(setting.createdAt).toLocaleString('ru-RU')}
                      </p>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {!setting.isActive && (
                        <Button
                          onClick={() => handleActivateSettings(setting.id)}
                          variant="default"
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                        >
                          Активировать
                        </Button>
                      )}
                      
                      <Button
                        onClick={() => startEditSettings(setting)}
                        variant="default"
                        size="sm"
                      >
                        Редактировать
                      </Button>
                      
                      {!setting.isActive && (
                        <Button
                          onClick={() => handleDeleteSettings(setting.id)}
                          variant="destructive"
                          size="sm"
                        >
                          Удалить
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

export default SLASettingsPage;
